import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { PageContainer } from '../../Styles/AppStyles';
import { Header } from '../../Components/Header/Header';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { RotateCcwIcon } from 'lucide-react';
import ConfirmationModal from '../../Components/Modal/ConfirmationModal';
import apiClient from '../../Services/ApiClient';
import { useSnackbar } from '../../Context/SnackbarProvider';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import { SearchableHeader } from '../../Components/Search/SearchableHeader';
import { useColumnSearches } from '../../Hooks/useColumnSearch';

const RestoreButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 4px;
  border: 1px solid #0098aa;
  color: #0098aa;
  background: transparent;
  cursor: pointer;
  &:hover {
    background: #0098aa15;
  }
`;

const NonAbatixDeletedPage: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [restoreTargetId, setRestoreTargetId] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const [selectedOvs, setSelectedOvs] = useState<string>('');
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const searchFields = ['product_code', 'description', 'category_name', 'unit_of_measure', 'location_name', 'CustomerName'];
  const { columnSearches, setSearch, clearSearch } = useColumnSearches(searchFields);

  const handleColumnSearch = (field: string, val: string) => setSearch(field, val);
  const handleClearColumnSearch = (field: string) => clearSearch(field);
  const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
    setSortField(field);
    setSortOrder(order ?? 'asc');
  };

  const deletedInventoryUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedOvs && selectedOvs !== '') params.set('location_id', selectedOvs);
    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });
    if (sortField) {
      params.set('sort_field', sortField);
      params.set('sort_order', sortOrder);
    }
    const qs = params.toString();
    return qs ? `/api/non-abatix/inventory/deleted?${qs}` : '/api/non-abatix/inventory/deleted';
  }, [selectedOvs, columnSearches, sortField, sortOrder]);

  const [deletedItems, loading, , refetch] = useGetCustomData(deletedInventoryUrl);

  const rows: any[] = useMemo(
    () => (Array.isArray(deletedItems) ? deletedItems : []),
    [deletedItems]
  );

  const columns: ColumnDef[] = [
    {
      field: 'CustomerName',
      headerName: 'Account',
      width: 180,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="CustomerName"
          headerName="Account"
          searchValue={columnSearches.CustomerName}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'location_name',
      headerName: 'Location',
      width: 180,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="location_name"
          headerName="Location"
          searchValue={columnSearches.location_name}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'product_code',
      headerName: 'Part No.',
      width: 160,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="product_code"
          headerName="Part No."
          searchValue={columnSearches.product_code}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 380,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="description"
          headerName="Description"
          searchValue={columnSearches.description}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'unit_of_measure',
      headerName: 'UOM',
      width: 100,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="unit_of_measure"
          headerName="UOM"
          searchValue={columnSearches.unit_of_measure}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'category_name',
      headerName: 'Category',
      width: 160,
      sortable: true,
      renderHeader: () => (
        <SearchableHeader
          field="category_name"
          headerName="Category"
          searchValue={columnSearches.category_name}
          onSearchChange={handleColumnSearch}
          onClearSearch={handleClearColumnSearch}
        />
      ),
    },
    {
      field: 'deleted_at',
      headerName: 'Last Modified',
      width: 180,
      sortable: true,
      valueGetter: (value: any) => value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC', month: '2-digit', day: '2-digit', year: 'numeric' }) : '-',
      renderCell: (params: any) => params.value,
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <RestoreButton onClick={() => setRestoreTargetId(String(params.row.id))}>
          <RotateCcwIcon size={14} />
          Restore
        </RestoreButton>
      ),
    },
  ];

  return (
    <PageContainer>
      <Header title="Deleted Items" count={rows.length} hideCount={false} />
      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        pageKey="non-abatix-deleted-inventory"
        hideColumnFilters={true}
        hideSearchBar={true}
        serverSideSort={true}
        onSortModelChange={handleSortChange}
      >
        <LocationSelect
          ovs={ovs}
          selectedOvs={selectedOvs}
          onChange={setSelectedOvs}
          setDefault={true}
          defaultName="All Locations"
        />
      </DataTable>
      {restoreTargetId && (
        <ConfirmationModal
          title="Restore Item?"
          message="This item will be restored and will appear in the location's inventory again."
          variant="primary"
          confirmText="Restore"
          isLoading={isRestoring}
          loadingText="Restoring..."
          icon="restore"
          onConfirm={async () => {
            if (isRestoring) return;
            setIsRestoring(true);
            try {
              const response = await apiClient(`/api/non-abatix/inventory/restore/${restoreTargetId}`, { method: 'PUT' });
              if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                showSnackbar(err.error || 'Failed to restore', 'error', 'Something went wrong!');
              } else {
                showSnackbar('Item restored successfully', 'success');
                refetch();
                setRestoreTargetId(null);
              }
            } finally {
              setIsRestoring(false);
            }
          }}
          onCancel={() => {
            if (!isRestoring) setRestoreTargetId(null);
          }}
        />
      )}
    </PageContainer>
  );
};

export default NonAbatixDeletedPage;
